import React, { useState, useRef, useEffect, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Pencil,
  Palette,
  Trash2,
  Mic,
  Play,
  XCircle,
  Undo2,
  Redo2,
  Save,
  Droplet,
  Brush,
  Eraser,
  Move,
  Cloud,
  Home,
  PaintBucket,
  Square,
  Circle,
  Triangle,
  Star,
  PlusCircle, // Shape icons & action icon
} from "lucide-react";

// --- Framer Motion Variants ---
const pageVariants = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0, transition: { duration: 0.7, ease: "easeOut" } },
  exit: { opacity: 0, y: 20, transition: { duration: 0.5, ease: "easeIn" } },
};

const controlButtonVariants = {
  rest: { scale: 1, transition: { duration: 0.2 } },
  hover: { scale: 1.05, boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.1)" },
  tap: { scale: 0.95 },
};

const modalVariants = {
  hidden: { opacity: 0, y: 50, scale: 0.8 },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: { duration: 0.3, ease: "easeOut" },
  },
  exit: {
    opacity: 0,
    y: 50,
    scale: 0.8,
    transition: { duration: 0.2, ease: "easeIn" },
  },
};

// --- Color Palette Options ---
const COLORS = [
  { name: "Black", hex: "#000000" },
  { name: "White", hex: "#FFFFFF" },
  { name: "Red", hex: "#EF4444" },
  { name: "Orange", hex: "#F97316" },
  { name: "Yellow", hex: "#FACC15" },
  { name: "Green", hex: "#22C55E" },
  { name: "Blue", hex: "#3B82F6" },
  { name: "Purple", hex: "#A855F7" },
  { name: "Pink", hex: "#EC4899" },
  { name: "Cyan", hex: "#06B6D4" },
];

// --- Shape Options ---
const SHAPES = [
  { name: "Rectangle", icon: Square, type: "rect" },
  { name: "Circle", icon: Circle, type: "circle" },
  { name: "Triangle", icon: Triangle, type: "triangle" },
  { name: "Star", icon: Star, type: "star" },
];

export default function CreateAndDrawPage() {
  const canvasRef = useRef(null);
  const [ctx, setCtx] = useState(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [color, setColor] = useState(COLORS[0].hex);
  const [brushSize, setBrushSize] = useState(5);
  const [tool, setTool] = useState("brush"); // 'brush', 'eraser', 'fill', 'shape'
  const [activeShape, setActiveShape] = useState(null); // Tracks the *selected* shape type for canvas placement
  const [history, setHistory] = useState([]);
  const [historyStep, setHistoryStep] = useState(0);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (canvas) {
      const context = canvas.getContext("2d");
      context.lineCap = "round";
      context.lineJoin = "round";
      setCtx(context);

      // Initialize canvas with white background
      context.fillStyle = "#FFFFFF";
      context.fillRect(0, 0, canvas.width, canvas.height);
      saveState(canvas.toDataURL());
    }
  }, []);

  // --- History Management ---
  const saveState = (dataURL) => {
    // Only save if history is about to change significantly
    if (history.length === 0 || history[history.length - 1] !== dataURL) {
      const newHistory = history.slice(0, historyStep + 1);
      setHistory([...newHistory, dataURL]);
      setHistoryStep(newHistory.length);
    }
  };

  const loadState = (dataURL) => {
    const canvas = canvasRef.current;
    if (ctx) {
      const img = new Image();
      img.src = dataURL;
      img.onload = () => {
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        ctx.drawImage(img, 0, 0);
      };
    }
  };

  const undo = () => {
    if (historyStep > 0) {
      const newStep = historyStep - 1;
      setHistoryStep(newStep);
      loadState(history[newStep]);
    }
  };

  const redo = () => {
    if (historyStep < history.length - 1) {
      const newStep = historyStep + 1;
      setHistoryStep(newStep);
      loadState(history[newStep]);
    }
  };

  // --- Core Drawing Logic ---
  const drawShape = (type, x, y) => {
    if (!ctx) return;

    ctx.fillStyle = color;
    ctx.strokeStyle = color;
    ctx.lineWidth = 2;

    const size = brushSize * 5;
    const halfSize = size / 2;

    ctx.beginPath();
    switch (type) {
      case "rect":
        ctx.fillRect(x - halfSize, y - halfSize, size, size);
        break;
      case "circle":
        ctx.arc(x, y, halfSize, 0, Math.PI * 2);
        ctx.fill();
        break;
      case "triangle":
        ctx.moveTo(x, y - halfSize);
        ctx.lineTo(x - halfSize, y + halfSize);
        ctx.lineTo(x + halfSize, y + halfSize);
        ctx.closePath();
        ctx.fill();
        break;
      case "star":
        const outerRadius = halfSize;
        const innerRadius = halfSize / 2;
        const spikes = 5;
        for (let i = 0; i < spikes; i++) {
          let outerAngle = (Math.PI / spikes) * (2 * i) - Math.PI / 2;
          let innerAngle = (Math.PI / spikes) * (2 * i + 1) - Math.PI / 2;
          ctx.lineTo(
            x + outerRadius * Math.cos(outerAngle),
            y + outerRadius * Math.sin(outerAngle)
          );
          ctx.lineTo(
            x + innerRadius * Math.cos(innerAngle),
            y + innerRadius * Math.sin(innerAngle)
          );
        }
        ctx.closePath();
        ctx.fill();
        break;
      default:
        break;
    }
    // 1. Save the state immediately after drawing the shape
    saveState(canvasRef.current.toDataURL());
    // 2. Reset the active shape
    setActiveShape(null);
    // 3. KEY FIX: Reset the tool to 'brush' to prevent the modal from reopening
    setTool("brush");
  };

  const handleInteraction = ({ nativeEvent }) => {
    const { offsetX, offsetY } = nativeEvent;

    if (!ctx) return;

    if (tool === "fill") {
      // --- FILL LOGIC ---
      ctx.fillStyle = color;
      ctx.fillRect(0, 0, canvasRef.current.width, canvasRef.current.height);
      saveState(canvasRef.current.toDataURL());
      return;
    }

    if (tool === "shape" && activeShape) {
      // --- SHAPE PLACEMENT LOGIC ---
      // If an active shape is set, draw it at the click coordinates
      drawShape(activeShape, offsetX, offsetY);
      // The drawShape function now handles resetting the tool/activeShape
      return;
    }

    // --- DRAWING LOGIC (Brush/Eraser) ---
    if (nativeEvent.type === "mousedown" || nativeEvent.type === "touchstart") {
      ctx.beginPath();
      ctx.moveTo(offsetX, offsetY);
      setIsDrawing(true);
    }
  };

  const draw = ({ nativeEvent }) => {
    // Only draw if we are actively drawing a line (brush/eraser)
    if (!isDrawing || tool === "fill" || tool === "shape") return;
    const { offsetX, offsetY } = nativeEvent;

    ctx.strokeStyle = tool === "brush" ? color : "#FFFFFF"; // Eraser uses white
    ctx.lineWidth = brushSize;
    ctx.lineTo(offsetX, offsetY);
    ctx.stroke();
  };

  const stopDrawing = () => {
    // Only save history if we were drawing a line
    if (isDrawing && tool !== "fill" && tool !== "shape") {
      ctx.closePath();
      setIsDrawing(false);
      saveState(canvasRef.current.toDataURL());
    }
  };

  const clearCanvas = () => {
    if (ctx && window.confirm("Are you sure you want to clear the canvas?")) {
      ctx.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
      ctx.fillStyle = "#FFFFFF"; // Fill with white after clearing
      ctx.fillRect(0, 0, canvasRef.current.width, canvasRef.current.height);
      saveState(canvasRef.current.toDataURL());
    }
  };

  const handleDownload = () => {
    const canvas = canvasRef.current;
    const image = canvas.toDataURL("image/png");
    const link = document.createElement("a");
    link.href = image;
    link.download = "talkytoon-drawing.png";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Voice Command placeholder
  const startVoiceDrawing = () => {
    alert(
      "Voice drawing activated! Say commands like 'draw red circle' or 'undo'."
    );
  };

  // Determine cursor style
  const cursorStyle = useMemo(() => {
    if (tool === "fill") return "cursor-alias";
    // If tool is 'shape' AND a shape is selected, show crosshair for placement
    if (tool === "shape" && activeShape) return "cursor-crosshair";
    return "cursor-crosshair";
  }, [tool, activeShape]);

  return (
    <motion.div
      className="min-h-screen bg-gradient-to-br from-purple-50 to-indigo-100 p-4 sm:p-6 lg:p-8 flex flex-col items-center relative overflow-hidden"
      variants={pageVariants}
      initial="initial"
      animate="animate"
      exit="exit"
    >
      {/* Background Bubbles/Shapes */}
      <motion.div
        className="absolute top-1/4 left-10 w-24 h-24 bg-pink-300 rounded-full opacity-30 animate-blob"
        animate={{ x: [0, 50, 0], y: [0, -30, 0] }}
        transition={{ duration: 15, repeat: Infinity }}
      />
      <motion.div
        className="absolute bottom-1/4 right-10 w-32 h-32 bg-yellow-300 rounded-full opacity-30 animate-blob animation-delay-2000"
        animate={{ x: [0, -40, 0], y: [0, 60, 0] }}
        transition={{ duration: 18, repeat: Infinity }}
      />
      <motion.div
        className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-40 h-40 bg-blue-300 rounded-full opacity-20 animate-blob animation-delay-4000"
        animate={{ x: [0, 70, 0], y: [0, -50, 0] }}
        transition={{ duration: 20, repeat: Infinity }}
      />

      <h1 className="text-4xl md:text-5xl font-extrabold text-center mb-6 z-10 text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-indigo-500 drop-shadow-md">
        The Voice Art Studio! 🎨
      </h1>
      <p className="text-lg md:text-xl text-gray-700 text-center mb-8 max-w-2xl z-10">
        Time to create! Use brushes, colors, shapes, and the fill tool to design
        your scene.
      </p>

      {/* --- MAIN CONTENT: Order for Mobile (Canvas Up, Tools Down) --- */}
      <div className="flex flex-col lg:flex-row gap-6 w-full max-w-6xl z-10">
        {/* Right Area: Canvas & Voice Controls (order-1: UP on Mobile) */}
        <div className="flex-grow flex flex-col space-y-4 order-1 lg:order-2">
          {/* Voice Command Section */}
          <motion.div
            className="bg-white p-4 rounded-xl shadow-lg flex flex-col sm:flex-row items-center justify-between gap-4"
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3, duration: 0.5 }}
          >
            <div className="flex items-center gap-3">
              <Mic className="w-7 h-7 text-indigo-500" />
              <p className="text-lg font-semibold text-gray-800">
                Voice Control
              </p>
            </div>
            <motion.button
              variants={controlButtonVariants}
              whileHover="hover"
              whileTap="tap"
              onClick={startVoiceDrawing}
              className="flex items-center gap-2 px-5 py-2.5 bg-indigo-500 hover:bg-indigo-600 rounded-full font-bold text-white shadow-md transition-colors"
              aria-label="Start Voice Drawing"
            >
              <Play className="w-5 h-5" /> Speak to Draw!
            </motion.button>
          </motion.div>

          {/* Action Buttons (above canvas) */}
          <motion.div
            className="grid grid-cols-4 sm:grid-cols-5 gap-2 bg-white p-3 rounded-xl shadow-lg"
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.35, duration: 0.5 }}
          >
            <ActionButton
              icon={Undo2}
              label="Undo"
              action={undo}
              color="blue"
              disabled={historyStep === 0}
            />
            <ActionButton
              icon={Redo2}
              label="Redo"
              action={redo}
              color="blue"
              disabled={historyStep === history.length - 1}
            />
            {/* The action here sets tool to 'shape', which opens the modal due to state change */}
            <ActionButton
              icon={Square}
              label="Shapes"
              action={() => setTool("shape")}
              color="purple"
            />
            <ActionButton
              icon={Trash2}
              label="Clear All"
              action={clearCanvas}
              color="red"
            />
            <ActionButton
              icon={Save}
              label="Save"
              action={handleDownload}
              color="green"
            />
          </motion.div>

          {/* Drawing Canvas */}
          <motion.div
            className="relative bg-gray-100 border-4 border-dashed border-indigo-200 rounded-xl shadow-lg overflow-hidden flex-grow"
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.4, duration: 0.6 }}
            style={{ minHeight: "400px" }}
          >
            <canvas
              ref={canvasRef}
              width={800}
              height={600}
              onMouseDown={handleInteraction}
              onMouseMove={draw}
              onMouseUp={stopDrawing}
              onMouseLeave={stopDrawing}
              onTouchStart={handleInteraction}
              onTouchMove={draw}
              onTouchEnd={stopDrawing}
              className={`w-full h-full ${cursorStyle}`}
            ></canvas>
            <div className="absolute top-4 right-4 text-sm text-gray-500 bg-white/70 px-3 py-1 rounded-lg shadow-inner">
              Tool: <span className="font-semibold capitalize">{tool}</span>,
              Color:{" "}
              <span className="font-semibold" style={{ color: color }}>
                ●
              </span>
              {activeShape && `, Next: ${activeShape}`}
            </div>
          </motion.div>
        </div>

        {/* Left Sidebar: Drawing Controls (order-2: DOWN on Mobile) */}
        <motion.div
          className="w-full lg:w-64 bg-white p-4 rounded-xl shadow-lg flex flex-col space-y-4 h-fit order-2 lg:order-1"
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.2, duration: 0.5 }}
        >
          <h2 className="text-xl font-bold text-gray-800 border-b pb-2 mb-2">
            Tools
          </h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-2 gap-2">
            <ToolButton
              icon={Brush}
              label="Brush"
              currentTool={tool}
              setTool={setTool}
              toolName="brush"
            />
            <ToolButton
              icon={Eraser}
              label="Eraser"
              currentTool={tool}
              setTool={setTool}
              toolName="eraser"
            />
            <ToolButton
              icon={PaintBucket}
              label="Fill"
              currentTool={tool}
              setTool={setTool}
              toolName="fill"
            />
            <ToolButton
              icon={Square}
              label="Shapes"
              currentTool={tool}
              setTool={setTool}
              toolName="shape"
            />
          </div>

          <h2 className="text-xl font-bold text-gray-800 border-b pb-2 mb-2 pt-4">
            Colors
          </h2>
          <div className="grid grid-cols-5 gap-2">
            {COLORS.map((c) => (
              <motion.button
                key={c.name}
                variants={controlButtonVariants}
                whileHover="hover"
                whileTap="tap"
                onClick={() => setColor(c.hex)}
                className={`w-8 h-8 rounded-full border-2 ${
                  color === c.hex
                    ? "border-indigo-500 ring-2 ring-indigo-300"
                    : "border-gray-300"
                }`}
                style={{ backgroundColor: c.hex }}
                aria-label={`Select ${c.name} color`}
              />
            ))}
          </div>

          <h2 className="text-xl font-bold text-gray-800 border-b pb-2 mb-2 pt-4">
            Size
          </h2>
          <input
            type="range"
            min="1"
            max="30"
            value={brushSize}
            onChange={(e) => setBrushSize(parseInt(e.target.value))}
            disabled={tool === "fill" || tool === "shape"}
            className={`w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer range-lg focus:outline-none ${
              tool === "fill" || tool === "shape"
                ? "opacity-50"
                : "focus:ring-2 focus:ring-indigo-500"
            }`}
            aria-label="Brush/Shape Size Slider"
          />
          <span className="text-center text-sm text-gray-600">
            {tool === "fill"
              ? "N/A"
              : tool === "shape"
              ? `${brushSize * 5}px (shape)`
              : `${brushSize}px (brush)`}
          </span>
        </motion.div>
      </div>

      {/* Footer Navigation (optional) */}
      {/* <motion.div
        className="mt-12 w-full max-w-md bg-white p-4 rounded-xl shadow-lg flex justify-center gap-6 z-10"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.6, duration: 0.5 }}
      >
        <motion.button
          variants={controlButtonVariants}
          whileHover="hover"
          whileTap="tap"
          onClick={() => alert("Navigate to Games Page!")}
          className="flex items-center gap-2 px-5 py-2.5 bg-gray-200 hover:bg-gray-300 rounded-full font-semibold text-gray-700 transition-colors"
        >
          <Home className="w-5 h-5" /> Back to Adventures
        </motion.button>
      </motion.div> */}

      {/* Shapes Palette Modal */}
      <AnimatePresence>
        {/* Modal condition: Show if tool is 'shape' AND no shape is currently selected for placement */}
        {tool === "shape" && activeShape === null && (
          <ShapesPalette
            setActiveShape={setActiveShape}
            setTool={setTool}
            controlButtonVariants={controlButtonVariants}
          />
        )}
      </AnimatePresence>

      {/* Tailwind CSS for Custom Animations */}
      <style jsx>{`
        @keyframes blob {
          0% {
            transform: translate(0px, 0px) scale(1);
          }
          33% {
            transform: translate(30px, -50px) scale(1.1);
          }
          66% {
            transform: translate(-20px, 20px) scale(0.9);
          }
          100% {
            transform: translate(0px, 0px) scale(1);
          }
        }
        .animate-blob {
          animation: blob 7s infinite cubic-bezier(0.68, -0.55, 0.27, 1.55);
        }
        .animation-delay-2000 {
          animation-delay: 2s;
        }
        .animation-delay-4000 {
          animation-delay: 4s;
        }
      `}</style>
    </motion.div>
  );
}

// --- Helper Components for Clean Code ---

const ToolButton = ({ icon: Icon, label, currentTool, setTool, toolName }) => {
  const isActive = currentTool === toolName;
  return (
    <motion.button
      variants={controlButtonVariants}
      whileHover="hover"
      whileTap="tap"
      onClick={() => setTool(toolName)}
      className={`flex flex-col items-center justify-center p-2 rounded-lg text-xs font-semibold transition-colors h-14 ${
        isActive
          ? "bg-indigo-500 text-white shadow-md"
          : "bg-gray-200 text-gray-700 hover:bg-gray-300"
      }`}
      aria-label={`Select ${label} Tool`}
    >
      <Icon className="w-5 h-5 mb-1" /> {label}
    </motion.button>
  );
};

const ActionButton = ({
  icon: Icon,
  label,
  action,
  color,
  disabled = false,
}) => {
  const colorClasses = {
    blue: "bg-blue-100 text-blue-700 hover:bg-blue-200",
    red: "bg-red-100 text-red-700 hover:bg-red-200",
    green: "bg-green-100 text-green-700 hover:bg-green-200",
    purple: "bg-purple-100 text-purple-700 hover:bg-purple-200",
  };

  return (
    <motion.button
      variants={controlButtonVariants}
      whileHover="hover"
      whileTap="tap"
      onClick={action}
      disabled={disabled}
      className={`flex items-center justify-center p-2 rounded-lg text-sm font-semibold transition-colors ${colorClasses[color]} disabled:opacity-50 disabled:cursor-not-allowed`}
      aria-label={label}
    >
      <Icon className="w-5 h-5 mr-1" />{" "}
      <span className="hidden sm:inline">{label}</span>
    </motion.button>
  );
};

// --- Shapes Palette Modal Component (Updated Logic) ---
const ShapesPalette = ({ setActiveShape, setTool, controlButtonVariants }) => {
  // Function to close the modal and switch back to the brush tool
  const closeModal = () => setTool("brush");

  return (
    <motion.div
      className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center p-4 z-50"
      variants={modalVariants}
      initial="hidden"
      animate="visible"
      exit="exit"
    >
      <motion.div
        className="bg-white rounded-xl shadow-2xl p-6 w-full max-w-sm relative"
        initial={{ scale: 0.9 }}
        animate={{ scale: 1 }}
        exit={{ scale: 0.9 }}
        transition={{ duration: 0.2 }}
      >
        <h3 className="text-2xl font-bold text-gray-800 mb-4 text-center">
          Choose a Shape!
        </h3>
        <p className="text-sm text-center text-gray-500 mb-6">
          Select a shape, then click on the canvas to place it.
        </p>
        <div className="grid grid-cols-2 gap-4">
          {SHAPES.map((shape) => {
            const ShapeIcon = shape.icon;
            return (
              <motion.button
                key={shape.type}
                variants={controlButtonVariants}
                whileHover="hover"
                whileTap="tap"
                onClick={() => {
                  setActiveShape(shape.type); // Set the shape to be drawn
                  // Tool remains 'shape', the modal will close because activeShape is no longer null
                }}
                className="flex flex-col items-center justify-center p-4 rounded-lg bg-indigo-100 text-indigo-700 hover:bg-indigo-200 transition-colors shadow-sm"
                aria-label={`Draw ${shape.name}`}
              >
                <ShapeIcon className="w-8 h-8 mb-2" />
                <span className="font-semibold text-sm">{shape.name}</span>
              </motion.button>
            );
          })}
        </div>
        <motion.button
          variants={controlButtonVariants}
          whileHover="hover"
          whileTap="tap"
          onClick={closeModal}
          className="absolute top-3 right-3 p-2 rounded-full bg-gray-200 hover:bg-gray-300 text-gray-600"
          aria-label="Close shape selection"
        >
          <XCircle className="w-6 h-6" />
        </motion.button>
      </motion.div>
    </motion.div>
  );
};
